import React from 'react'
import { styled, forwardRef, FloraVariants } from '@grupoboticario/flora-react-system'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

const StyledCheckbox = styled(CheckboxPrimitive.Root, {
  appearance: 'none',
  backgroundColor: 'transparent',
  border: 'none',
  padding: 0,
  width: 16,
  minWidth: 16,
  height: 16,
  borderRadius: '$small',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '$actionableDefault',
  boxShadow: 'inset 0 0 0 2px $colors$nonInteractivePredominant',
  '&:not(:disabled), &:not(:disabled) + label': {
    cursor: 'pointer',
  },
  '&:hover': {
    boxShadow: 'inset 0 0 0 2px $colors$actionableHover',
    '& > span': {
      color: '$colors$actionableHover',
    },
  },
  '&:focus': {
    outline: 'none',
  },
  variants: {
    size: {
      small: {
        '& + label': {
          fontSize: '$auxiliar',
        },
      },
      medium: {
        '& + label': {
          fontSize: '$bodySmall',
        },
      },
      large: {
        '& + label': {
          fontSize: '$bodyLarge',
        },
      },
    },
    variant: {
      alt: {
        '& + label': {
          color: '$colors$nonInteractiveAltPredominant',
        },
        boxShadow: 'inset 0 0 0 2px $colors$nonInteractiveAltPredominant',
        '& > span': {
          color: '$actionableAlt',
        },
        '&:hover': {
          boxShadow: 'inset 0 0 0 2px $colors$actionableAltHover',
          '& > span': {
            color: '$colors$actionableAltHover',
          },
        },
        '&[data-disabled]': {
          color: '$colors$disabledAltContent',
          backgroundColor: '$colors$disabledAltBackground',
          boxShadow: 'inset 0 0 0 2px $colors$disabledAltContent',
          '& > span, & + label, & > svg': {
            color: '$colors$disabledAltContent',
          },
        },
      },
      standard: {
        color: '$nonInteractivePredominant',
        boxShadow: 'inset 0 0 0 2px $colors$nonInteractivePredominant',
        '& > span': {
          color: '$actionableDefault',
        },
        '&:hover': {
          boxShadow: 'inset 0 0 0 2px $colors$actionableHover',
          '& > span': {
            color: '$colors$actionableHover',
          },
        },
        '&[data-disabled]': {
          color: '$disabledContent',
          backgroundColor: '$colors$disabledBackground',
          boxShadow: 'inset 0 0 0 2px $colors$disabledContent',
          '& > span, & + label, & > svg': {
            color: '$colors$disabledContent',
          },
        },
      },
    },
  },
  defaultVariants: {
    size: 'standard',
  },
})

const StyledCheckboxIndicator = styled(CheckboxPrimitive.Indicator, {
  display: 'flex',
})

/**
 * @deprecated Esta tipagem refere-se a um componente que será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-floracheckbox-guia-de-migra%C3%A7%C3%A3o--page| Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export type CheckboxProps = HTMLFloraProps<'button'> &
  CheckboxPrimitive.CheckboxProps &
  FloraVariants<typeof StyledCheckbox>

/**
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-florabutton-guia-de-migra%C3%A7%C3%A3o--page| Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export const Checkbox = forwardRef<'button', CheckboxProps>((props, forwardedRef) => {
  return (
    <StyledCheckbox data-flora="checkbox" ref={forwardedRef} {...props}>
      <StyledCheckboxIndicator>
        {props.checked === 'indeterminate' ? (
          <svg
            width="8"
            height="4"
            viewBox="0 0 8 2"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M0 1H8" stroke="currentColor" strokeWidth="3" />
          </svg>
        ) : (
          <svg
            width="12"
            height="10"
            viewBox="0 0 12 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 5.28571L4.8 7L9 3"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="square"
            />
          </svg>
        )}
      </StyledCheckboxIndicator>
    </StyledCheckbox>
  )
})
